import { nanoid } from 'nanoid';
import * as clipboard from './utils/clipboard.js';
import { getPhotoShareDomain } from '../../../packages/src/utils/domain.js';

let isSharingInProgress = false;

export function shareUrl({ text, url }) {
  if (isSharingInProgress) {
    console.warn('Share is already in progress.');
    return;
  }

  const preparedText = text ? `${text} ${url}` : url;

  try {
    if (navigator.canShare && navigator.canShare({ text: preparedText })) {
      isSharingInProgress = true;
      navigator.share({ text: preparedText });
    } else {
      clipboard.add(preparedText);
      window.prompt('Copy the link', preparedText);
    }
  } catch (error) {
    if (error.name === 'AbortError' || error.message.includes('cancellation of share')) {
      console.warn('Share was cancelled by the user or due to a system restriction:', error);
    } else if (error.name === 'InvalidStateError' && error.message.includes('share() is already in progress')) {
      console.warn('Share is already in progress. Please wait until it is complete.');
    } else {
      throw error;
    }
  } finally {
    isSharingInProgress = false;
  }
}

/**
 * Create object file as html file
 *
 * @param {string} htmlContent should be html content
 * @param {string} fileName like this 'XJIQILHHfIdg0'
 * @returns {File}
 */
function createHTMLFile(htmlContent, fileName = 'index') {
  const blob = new Blob([htmlContent], { type: 'text/html' });
  return new File([blob], `${fileName}.html`, { type: 'text/html' });
}

function generateShareToken(attendeeUid) {
  return `${attendeeUid.replace('-', '').slice(0, 8)}${nanoid(5)}`;
}

function htmlPageContent({ ogpData, eventCode, photoData, localizationContext } = {}) {
  const baseUrl = getPhotoShareDomain();
  const eventUrlJson = `${baseUrl}/data/event-${eventCode}.json`;
  const photoDataJSON = JSON.stringify(photoData);
  const localizationContextJSON = JSON.stringify(localizationContext ?? null);
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
  <meta property="og:title" content="${ogpData.title ?? ''}" />
  <meta property="og:description" content="${ogpData.description ?? ''}" />
  <meta property="og:image" content="${ogpData.image}" />
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700&display=swap" rel="stylesheet">
  <link
    href="https://cdn.jsdelivr.net/npm/bootstrap@5.0.1/dist/css/bootstrap.min.css"
    rel="stylesheet"
    integrity="sha384-+0n0xVW2eSR5OomGNYDnhzAbDsOXxcvSN1TPprVMTNDbiYZCxYbOOl7+AMvyTG2x"
    crossorigin="anonymous"
  />
  <title>${ogpData.title ?? ''}</title>
  <script defer="defer" src="../static/index.js"></script>
  <link href="../static/main.css" rel="stylesheet">
</head>

<body class="body body_background_primary">
<div class="page page_main" style="padding-bottom:84px;">
  <div class="top-panel top-panel_selected top-panel_hidden">
    <div class="top-panel__left">
      <div class="top-panel__selected-text"></div>
    </div>
    <div class="top-panel__right">
      <button type="button" class="top-panel__btn-clear" data-loc-text="clear-btn"></button>
    </div>
  </div>
  <div class="page__inner">
    <div class="banner-position banner-position_top"></div>
    <div class="empty-content" style="display:none;">
      <img class="empty-content__icon" src="../static/img/icon-empty-gallery.svg" />
      <div class="empty-content__text" data-loc-text="home-empty-album-message"></div>
      <div class="empty-content__subtext"></div>
    </div>
    <div class="gallery" style="display:none;">

    </div>
    <div class="banner-position banner-position_bottom"></div>
  </div>
  <div class="action-panel">
  </div>
  <div class="float-button float-button_custom_main-page">
  </div>
</div>

<div class="full-screen-view">
  <div class="full-screen-view__top-panel">
    <div class="top-panel top-panel_full-screen-style">
      <div class="top-panel__title-center">
        <div class="top-panel__menu-text"></div>
      </div>
      <div class="top-panel__title-center-right">
        <button type="button" class="top-panel__btn-close close-full-screen-view"><img src="../static/img/close-page-modal-white.svg"></button>
      </div>
    </div>
  </div>
  <div class="full-screen-view__list">
  </div>
  <div class="full-screen-view__nav">
    <img class="full-screen-view__nav-btn full-screen-view__nav-btn-prev" src="../static/img/arrow-left.svg" />
    <img class="full-screen-view__nav-btn full-screen-view__nav-btn-next" src="../static/img/arrow-right.svg" />
  </div>
  <div class="full-screen-view__buttons">
    <button class="full-screen-view__button full-screen-view__button_share">
      <img class="full-screen-view__btn-img" alt="" src="../static/img/menu-share.svg"/>
      <span class="full-screen-view__btn-text" data-loc-text="share-btn"></span>
    </button>
    <button class="full-screen-view__button full-screen-view__button_download">
      <img class="full-screen-view__btn-img" alt="" src="../static/img/menu-download.svg"/>
      <span class="full-screen-view__btn-text" data-loc-text="download-btn"></span>
    </button>
    <button class="full-screen-view__button full-screen-view__button_favorite" aria-label="Favorite">
      <i class="far fa-heart full-screen-view__btn-img"></i>
      <span class="full-screen-view__btn-text" data-loc-text="favorite-btn"></span>
    </button>
  </div>
  <div class="full-screen-view__bottom-panel"></div>
</div>
<div class="screenOverlay">
  <div id="cssload-cupcake" class="cssload-box">
    <div class="cssload-cupcakeCircle cssload-box">
      <div class="cssload-cupcakeInner cssload-box">
        <div class="cssload-cupcakeCore cssload-box"></div>
      </div>
    </div>
  </div>
</div>
<!-- Message popUp  -->
<div
  id="messagePopUp"
  class="modal fade"
  tabindex="-1"
  aria-hidden="true"
  role="dialog"
>
  <div class="modal-dialog modal-dialog-centered">
    <!-- Modal content-->
    <div class="modal-content modal-dialog-centered">
      <div class="modal-header">
        <h5 class="modal-title" id="messagePopUpTitle"></h5>
      </div>
      <div id="messagePopUpBody" class="modal-body"></div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" data-dismiss="modal" data-loc-text="close-btn"></button>
      </div>
    </div>
  </div>
</div>
<!-- Confirm popUp  -->
<div
  id="confirmPopUp"
  class="modal fade"
  tabindex="-1"
  aria-hidden="true"
  role="dialog"
>
  <div class="modal-dialog modal-dialog-centered">
    <!-- Modal content-->
    <div class="modal-content modal-dialog-centered">
      <div class="modal-header">
        <h5 class="modal-title" id="confirmPopUpTitle"></h5>
        <button
          type="button"
          class="close"
          aria-label="Close"
          data-loc-aria-label="close-btn"
          data-dismiss="modal"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div
        id="confirmPopUpBody"
        class="modal-body"
        style="text-align:left"
      ></div>
      <div class="modal-footer">
        <button type="button" class="btn btn-sm" data-dismiss="modal" data-loc-text="cancel-btn"></button>
        <button
          type="button"
          id="confirmCallback"
          class="btn btn-sm btn-danger"
          data-dismiss="modal"
          data-loc-text="delete-btn"
        ></button>
      </div>
    </div>
  </div>
</div>
<!-- custom popUp  -->
<div
  id="customPopUp"
  class="modal fade"
  tabindex="-1"
  aria-hidden="true"
  role="dialog"
  data-backdrop="static"
  data-keyboard="false"
>
  <div class="modal-dialog modal-dialog-centered">
    <!-- Modal content-->
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title"></h5>
        <img class="close" data-dismiss="modal" src="../static/img/close.png" />
      </div>
      <div id="customPopUpBody" class="modal-body"></div>
    </div>
  </div>
</div>

<div class="waiting-notice waiting-notice_center_pad" style="display:none;">
  <img class="waiting-notice__img" src="../static/img/rolling.svg" alt="loader" />
  <div class="waiting-notice__text"></div>
</div>
<script>
  window.eventUrlJson = '${eventUrlJson}';
  window.photoData = JSON.parse('${photoDataJSON}');
  window.localizationContext = ${localizationContextJSON};
</script>
</body>
</html>
  `;
}

export function prepareSharePage({ ogpData, eventCode, attendeeUid, photoData, localizationContext }) {
  return createHTMLFile(
    htmlPageContent({
      ogpData,
      eventCode,
      photoData,
      localizationContext,
    }),
    generateShareToken(attendeeUid)
  );
}
